"use client";

import { Link } from "@/i18n/routing";
import { useSearchParams, usePathname } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

export function Pagination({ currentPage, totalPages }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const tc = useTranslations("common");

  if (totalPages <= 1) return null;

  function buildHref(page: number) {
    const params = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  // Build page number list with ellipsis
  function getPages(): (number | "...")[] {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | "...")[] = [1];
    if (currentPage > 3) pages.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  }

  const pages = getPages();
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const linkBase =
    "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm px-2 text-sm transition-colors duration-150";
  const activeLink =
    "bg-brand-dark text-white font-medium";
  const inactiveLink =
    "border border-brand-dark/20 text-brand-dark hover:border-brand-dark/60 hover:bg-brand-dark/5";
  const disabledLink =
    "border border-brand-dark/10 text-brand-dark/30 cursor-not-allowed";

  return (
    <nav
      aria-label={tc("pagination")}
      className="mt-12 flex items-center justify-center gap-1.5"
    >
      {/* Previous */}
      {hasPrev ? (
        <Link
          href={buildHref(currentPage - 1)}
          scroll={false}
          onClick={() => trackEvent("pagination_click", { page: currentPage - 1, totalPages })}
          className={cn(linkBase, inactiveLink)}
          aria-label={tc("previousPage")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Link>
      ) : (
        <span className={cn(linkBase, disabledLink)} aria-disabled="true">
          <ChevronLeft className="h-4 w-4" />
        </span>
      )}

      {/* Page numbers */}
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="px-1 text-sm text-brand-muted">
            &hellip;
          </span>
        ) : (
          <Link
            key={p}
            href={buildHref(p)}
            scroll={false}
            onClick={() => trackEvent("pagination_click", { page: p, totalPages })}
            aria-current={p === currentPage ? "page" : undefined}
            className={cn(
              linkBase,
              p === currentPage ? activeLink : inactiveLink
            )}
          >
            {p}
          </Link>
        )
      )}

      {/* Next */}
      {hasNext ? (
        <Link
          href={buildHref(currentPage + 1)}
          scroll={false}
          onClick={() => trackEvent("pagination_click", { page: currentPage + 1, totalPages })}
          className={cn(linkBase, inactiveLink)}
          aria-label={tc("nextPage")}
        >
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : (
        <span className={cn(linkBase, disabledLink)} aria-disabled="true">
          <ChevronRight className="h-4 w-4" />
        </span>
      )}
    </nav>
  );
}
